import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { NotificationService } from '@app/core/services/infrastructure/notification/notification.service';
import { ShareApiService, shareUrl } from '@app/shared/services/share-api.service';
import { IShareLink, ShareKind } from '@app/shared/interfaces/share.interface';

export interface IShareDialogData {
    kind: ShareKind;
    /** "Training Di 18:00", "KW 45", "Hinrunde", a session's first drill — used in the dialog and the message. */
    label: string;
    /** Creates the link, or returns the existing one. */
    create: () => Observable<IShareLink>;
}

/**
 * Sharing a plan, week or period (from the calendar or a session): the link, ready-made for WhatsApp, e-mail or the phone's share sheet,
 * how often it was opened (and taken over), and ending it.
 */
@Component({
    selector: 'app-share-dialog',
    templateUrl: './share-dialog.component.html',
    styleUrl: './share-dialog.component.scss',
    standalone: false,
})
export class ShareDialogComponent implements OnInit {
    link: IShareLink | null = null;
    failed = false;
    isBusy = false;
    readonly canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: IShareDialogData,
        private readonly dialogRef: MatDialogRef<ShareDialogComponent>,
        private readonly shareApi: ShareApiService,
        private readonly notificationService: NotificationService,
        private readonly translate: TranslateService
    ) {}

    ngOnInit(): void {
        this.data.create().subscribe({
            next: (link) => (this.link = link),
            error: () => (this.failed = true),
        });
    }

    get url(): string {
        return this.link ? shareUrl(this.link.token) : '';
    }

    /** The message that goes with the link. */
    get message(): string {
        return this.translate.instant(`share.dialog.message.${this.data.kind}`, { label: this.data.label, url: this.url });
    }

    get whatsappUrl(): string {
        return `https://wa.me/?text=${encodeURIComponent(this.message)}`;
    }

    get mailUrl(): string {
        const subject = this.translate.instant(`share.dialog.subject.${this.data.kind}`, { label: this.data.label });
        return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(this.message)}`;
    }

    copy(): void {
        navigator.clipboard?.writeText(this.url).then(
            () => this.notificationService.open('share.dialog.copied'),
            () => this.notificationService.error('share.errors.copy')
        );
    }

    nativeShare(): void {
        navigator.share?.({ title: this.data.label, text: this.message }).catch(() => undefined);
    }

    revoke(): void {
        if (!this.link || this.isBusy) return;
        this.isBusy = true;
        this.shareApi.revoke(this.link.token).subscribe({
            next: () => {
                this.notificationService.open('share.dialog.revoked');
                this.dialogRef.close();
            },
            error: () => {
                this.isBusy = false;
                this.notificationService.error('share.errors.revoke');
            },
        });
    }

    close(): void {
        this.dialogRef.close();
    }
}
